import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiShare2, FiEdit2, FiRotateCcw, FiEye, FiCheck, FiX, FiList } from 'react-icons/fi';
import {
  storageBoxApi,
  isPreviewableImage,
  isPreviewableVideo,
  isPreviewablePdf,
  isPreviewableText,
  isPreviewableOfficeDocument,
  getOfficeDocumentKind,
  officeDocumentPreviewLabel,
  formatBytes,
  isPreviewableAudio,
} from '../api/storageBoxApi';
import { STORAGE_BOX_ROOT_LABEL, STORAGE_BOX_BIN_RETENTION_DAYS } from '../constants';
import CopyShareLinkButton from './CopyShareLinkButton';
import StorageBoxPreviewOverview from './StorageBoxPreviewOverview';
import { useStorageBox } from '../StorageBoxContext';
import StorageBoxItemMetaControls from './StorageBoxItemMetaControls';
import { colorTagDisplayName, colorTagHex } from '../utils/itemMetaDisplay';
import { buildStorageBoxShareLinkForShare, rememberShareLinkToken } from '../utils/shareLinkUrl';
import { StorageBoxFolderIcon } from '../utils/storageBoxFolderIcon';
import { StorageBoxPreviewSkeleton } from '../../layout/ReusableComponent/Skeleton/RouteSkeletons';
import { MobileMediaViewerPdf } from '../../layout/ReusableComponent/MobileMediaViewer';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '—';
  }
}

export default function PreviewPanel({
  className = '',
  item,
  publicToken,
  readOnly,
  onShare,
  onOpenMedia,
  onOpenAudio,
  onQueueAudioPlayNext,
  onOpenTextEditor,
}) {
  const {
    notify,
    browseMode,
    lockSessionToken,
    inLockVault,
    writable,
    lockMasterKeyReady,
    restoreBinItem,
    restoreBusyId,
    renameItem,
  } = useStorageBox();
  const inBin = browseMode === 'bin';
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const renameInputRef = useRef(null);
  useEffect(() => {
    setDetails(null);
    if (!item?.itemId || publicToken) return;
    let cancelled = false;
    setDetailsLoading(true);
    storageBoxApi
      .getItemDetails(item.itemId)
      .then((r) => {
        if (!cancelled) {
          (r.shares || []).forEach((s) => {
            if (s.kind === 'link' && s.linkToken && s.shareId) rememberShareLinkToken(s.shareId, s.linkToken);
          });
          setDetails(r);
        }
      })
      .catch(() => {
        if (!cancelled) setDetails(null);
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item?.itemId, publicToken]);

  useEffect(() => {
    setRenaming(false);
    setRenameDraft('');
    setRenameBusy(false);
  }, [item?.itemId]);

  useEffect(() => {
    if (!renaming) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [renaming]);

  const refreshDetails = useCallback(async () => {
    if (!item?.itemId || publicToken) return;
    try {
      const r = await storageBoxApi.getItemDetails(item.itemId);
      (r.shares || []).forEach((s) => {
        if (s.kind === 'link' && s.linkToken && s.shareId) rememberShareLinkToken(s.shareId, s.linkToken);
      });
      setDetails(r);
    } catch {
      /* keep previous */
    }
  }, [item?.itemId, publicToken]);

  const handleRevokeShare = useCallback(
    async (shareId) => {
      try {
        await storageBoxApi.revokeShare(shareId);
        await refreshDetails();
        notify('Share revoked');
      } catch (e) {
        notify(e.message || 'Failed to revoke');
      }
    },
    [refreshDetails, notify]
  );

  const asideClass = ['sb-preview', 'sb-preview--details', className].filter(Boolean).join(' ');

  if (!item) {
    return <StorageBoxPreviewOverview className={className} />;
  }

  const merged = details?.item ? { ...details.item, ...item } : item;
  const shares = details?.shares || [];
  const location =
    merged.locationPath || details?.locationPath || (item.parentId ? '—' : STORAGE_BOX_ROOT_LABEL);
  const canManage = !readOnly && (merged.canManage || (inLockVault && writable));
  const canRestoreFromBin = !readOnly && writable && inBin;
  const restoreExpired = merged.daysUntilPurge != null && merged.daysUntilPurge <= 0;
  const restoreBusy = restoreBusyId === merged.itemId;
  const url =
    merged.type === 'file'
      ? storageBoxApi.downloadUrl(merged.itemId, {
          publicToken,
          lockSessionToken: inLockVault && lockSessionToken && merged.isEncrypted ? lockSessionToken : null,
        })
      : null;
  const isViewableInOverlay =
    merged.type === 'file' &&
    (isPreviewableImage(merged.mimeType, merged.name) ||
      isPreviewableVideo(merged.mimeType, merged.name) ||
      isPreviewablePdf(merged.mimeType, merged.name) ||
      isPreviewableOfficeDocument(merged.mimeType, merged.name));

  const isAudioFile =
    merged.type === 'file' && isPreviewableAudio(merged.mimeType, merged.name);

  const openInViewer = () => {
    if (isPreviewableAudio(merged.mimeType, merged.name) && onOpenAudio) {
      onOpenAudio(merged);
      return;
    }
    if (isViewableInOverlay && onOpenMedia) onOpenMedia(merged);
  };

  const officeKind = getOfficeDocumentKind(merged.mimeType, merged.name);
  const viewerButtonLabel = isPreviewableVideo(merged.mimeType, merged.name)
    ? 'Play video'
    : isPreviewableAudio(merged.mimeType, merged.name)
      ? 'Play audio'
      : isPreviewablePdf(merged.mimeType, merged.name)
        ? 'View PDF'
        : officeKind
          ? officeDocumentPreviewLabel(officeKind)
          : 'View image';

  const showViewerButton =
    isViewableInOverlay || isPreviewableAudio(merged.mimeType, merged.name);

  const isTextFile = merged.type === 'file' && isPreviewableText(merged.mimeType, merged.name);
  const textEditorLabel = 'Open in editor';

  const startRename = () => {
    if (!canManage || renameBusy) return;
    setRenameDraft(merged.name || '');
    setRenaming(true);
  };

  const cancelRename = () => {
    if (renameBusy) return;
    setRenaming(false);
    setRenameDraft('');
  };

  const submitRename = async () => {
    if (!renaming || renameBusy || !canManage) return;
    const trimmed = renameDraft.trim();
    if (!trimmed) return;
    if (trimmed === merged.name) {
      cancelRename();
      return;
    }
    setRenameBusy(true);
    try {
      await renameItem(merged, trimmed);
      setRenaming(false);
      setRenameDraft('');
    } catch (e) {
      notify(e.message || 'Rename failed');
    } finally {
      setRenameBusy(false);
    }
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelRename();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      submitRename();
    }
  };

  return (
    <aside className={asideClass}>
      <header className="sb-preview__head">
        {renaming && canManage ? (
          <>
            <input
              ref={renameInputRef}
              type="text"
              className="sb-preview__title-input"
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={handleRenameKeyDown}
              maxLength={240}
              disabled={renameBusy}
              aria-label={merged.type === 'folder' ? 'Folder name' : 'File name'}
            />
            <div className="sb-preview__head-actions">
              <button
                type="button"
                className="sb-preview__head-action sb-preview__head-action--save"
                onClick={submitRename}
                disabled={renameBusy || !renameDraft.trim()}
                title="Save"
                aria-label="Save name"
              >
                <FiCheck aria-hidden />
              </button>
              <button
                type="button"
                className="sb-preview__head-action"
                onClick={cancelRename}
                disabled={renameBusy}
                title="Cancel"
                aria-label="Cancel rename"
              >
                <FiX aria-hidden />
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="sb-preview__title" title={merged.name}>
              {merged.name}
            </h3>
            {!inBin && (canManage || showViewerButton || (isTextFile && onOpenTextEditor)) ? (
              <div className="sb-preview__head-actions">
                {isTextFile && onOpenTextEditor ? (
                  <button
                    type="button"
                    className="sb-preview__head-action"
                    onClick={() => onOpenTextEditor(merged)}
                    title={textEditorLabel}
                    aria-label={textEditorLabel}
                  >
                    <FiEdit2 aria-hidden />
                  </button>
                ) : null}
                {isAudioFile && onQueueAudioPlayNext && !inBin ? (
                  <button
                    type="button"
                    className="sb-preview__head-action"
                    onClick={() => onQueueAudioPlayNext(merged)}
                    title="Play next in queue"
                    aria-label="Play next in queue"
                  >
                    <FiList aria-hidden />
                  </button>
                ) : null}
                {showViewerButton ? (
                  <button
                    type="button"
                    className="sb-preview__head-action"
                    onClick={openInViewer}
                    title={viewerButtonLabel}
                    aria-label={viewerButtonLabel}
                  >
                    <FiEye aria-hidden />
                  </button>
                ) : null}
                {canManage ? (
                  <>
                    {!isTextFile ? (
                      <button
                        type="button"
                        className="sb-preview__head-action"
                        onClick={startRename}
                        title="Rename"
                        aria-label="Rename"
                      >
                        <FiEdit2 aria-hidden />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="sb-preview__head-action"
                      onClick={() => onShare?.(merged)}
                      title="Share"
                      aria-label="Share"
                    >
                      <FiShare2 aria-hidden />
                    </button>
                  </>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </header>

      <dl className="sb-preview__props">
        <div>
          <dt>Type</dt>
          <dd>{merged.type === 'folder' ? 'Folder' : merged.mimeType || 'File'}</dd>
        </div>
        <div>
          <dt>Created</dt>
          <dd>{formatWhen(merged.createdAt)}</dd>
        </div>
        <div>
          <dt>Created by</dt>
          <dd>{merged.createdByName || merged.createdBy || '—'}</dd>
        </div>
        {merged.type === 'file' ? (
          <div>
            <dt>Size</dt>
            <dd>{formatBytes(merged.sizeBytes)}</dd>
          </div>
        ) : null}
        <div>
          <dt>Last modified</dt>
          <dd>
            {formatWhen(merged.updatedAt || merged.createdAt)}
            {merged.lastEditedByName ? ` · ${merged.lastEditedByName}` : ''}
          </dd>
        </div>
        <div>
          <dt>Location</dt>
          <dd className="sb-preview__location">{location}</dd>
        </div>
        <div>
          <dt>Shared</dt>
          <dd>{merged.isShared || shares.length ? 'Yes' : 'No'}</dd>
        </div>
        <div>
          <dt>Favorite</dt>
          <dd>{merged.isFavorite ? 'Yes' : 'No'}</dd>
        </div>
        <div>
          <dt>Color tag</dt>
          <dd>
            {merged.colorTag ? (
              <span className="sb-preview__color-tag">
                <span
                  className="sb-color-swatch sb-color-swatch--md"
                  style={{ backgroundColor: colorTagHex(merged.colorTag) || undefined }}
                  aria-hidden
                />
                {colorTagDisplayName(merged.colorTag)}
              </span>
            ) : (
              '—'
            )}
          </dd>
        </div>
        <div>
          <dt>Label</dt>
          <dd>{merged.tagLabel || '—'}</dd>
        </div>
      </dl>

      {detailsLoading ? <StorageBoxPreviewSkeleton compact /> : null}

      {canManage && !inBin ? <StorageBoxItemMetaControls item={merged} variant="sidebar" /> : null}

      {canRestoreFromBin ? (
        <div className="sb-preview__actions sb-preview__actions--bin">
          <button
            type="button"
            className="sb-primary-btn sb-preview__action sb-preview__restore-btn"
            disabled={restoreExpired || restoreBusy}
            onClick={() => restoreBinItem(merged)}
          >
            <FiRotateCcw aria-hidden /> {restoreBusy ? 'Restoring…' : 'Restore'}
          </button>
          <p className="sb-preview__hint sb-preview__bin-retention">
            {restoreExpired
              ? 'Retention period ended. This item will be removed permanently.'
              : merged.daysUntilPurge != null
                ? `${merged.daysUntilPurge} day${merged.daysUntilPurge === 1 ? '' : 's'} left before permanent removal. Items stay in the Bin for up to ${STORAGE_BOX_BIN_RETENTION_DAYS} days.`
                : `Items stay in the Bin for up to ${STORAGE_BOX_BIN_RETENTION_DAYS} days before permanent removal.`}
          </p>
        </div>
      ) : null}

      {canManage && shares.length ? (
        <section className="sb-preview__shares">
          <h4>Shared with</h4>
          <ul className="sb-preview__share-list">
            {shares.map((s) => {
              const link = buildStorageBoxShareLinkForShare(s);
              return (
                <li key={s.shareId}>
                  <div className="sb-preview__share-row">
                    <span>
                      {s.kind === 'user'
                        ? `${s.granteeName || s.granteeUserId}${s.granteeEmail ? ` (${s.granteeEmail})` : ''}`
                        : s.linkLabel || 'Anyone with the link'}
                      {s.expiresAt ? ` · until ${formatWhen(s.expiresAt)}` : ' · no expiry'}
                    </span>
                    {s.kind === 'user' ? (
                      <button type="button" className="sb-revoke-share-btn" onClick={() => handleRevokeShare(s.shareId)}>
                        Revoke
                      </button>
                    ) : null}
                  </div>
                  {link ? (
                    <div className="sb-link-box sb-link-box--compact">
                      <input readOnly value={link} aria-label="Share link" onFocus={(e) => e.target.select()} />
                      <div className="sb-link-box__actions">
                        <CopyShareLinkButton text={link} />
                        <button type="button" className="sb-revoke-share-btn" onClick={() => handleRevokeShare(s.shareId)}>
                          Revoke
                        </button>
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <p className="sb-preview__hint">Use Share to add people, create a link, or revoke access.</p>
        </section>
      ) : null}

      {merged.type === 'file' && url ? (
        <div className="sb-preview__body">
          {isTextFile ? (
            <p className="sb-preview__hint">Text opens in the editor below the header. Use Close when you are done.</p>
          ) : null}
          {isPreviewablePdf(merged.mimeType, merged.name) &&
          url &&
          !(inLockVault && merged.isEncrypted) ? (
            <MobileMediaViewerPdf
              src={url}
              title={merged.name}
              iframeClassName="sb-preview__pdf mobile-media-viewer-overlay__pdf"
            />
          ) : null}
          {isPreviewablePdf(merged.mimeType, merged.name) &&
          inLockVault &&
          merged.isEncrypted &&
          !lockMasterKeyReady ? (
            <p className="sb-preview__empty">Unlock Lock folder to preview this PDF.</p>
          ) : null}
          {!isPreviewableImage(merged.mimeType, merged.name) &&
          !isPreviewableVideo(merged.mimeType, merged.name) &&
          !isPreviewablePdf(merged.mimeType, merged.name) &&
          !isPreviewableOfficeDocument(merged.mimeType, merged.name) &&
          !isPreviewableText(merged.mimeType, merged.name) &&
          !isPreviewableAudio(merged.mimeType, merged.name) ? (
            <p className="sb-preview__empty">No preview for this file type.</p>
          ) : null}
        </div>
      ) : merged.type === 'folder' ? (
        <div className="sb-preview__folder-icon-wrap" aria-hidden>
          <StorageBoxFolderIcon
            name={merged.name}
            folderColor={merged.folderColor}
            className="sb-preview__folder-icon"
          />
        </div>
      ) : null}
    </aside>
  );
}
